import { memo, useState } from 'react';
import { LANG_LABELS, downloadCode } from '../lib/render-helpers';
import { decodeGrounded, isGroundedContent } from '../lib/structured-output-schema';
import GroundedAnswerCard from './grounded-answer-card';

function ImageViewer({ src, alt, onClose }) {
  return (
    <div onClick={onClose} style={{
      position: 'fixed', top: 0, right: 0, bottom: 0, left: 'var(--sidebar-width, 50px)', zIndex: 2000,
      background: 'rgba(0,0,0,0.85)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
    }}>
      <img src={src} alt={alt} style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain', borderRadius: 8 }} />
    </div>
  );
}

function ClickableImage({ src, alt, thumbStyle }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <>
      <img src={src} alt={alt} style={{ ...thumbStyle, cursor: 'pointer' }} onClick={e => { e.stopPropagation(); setExpanded(true); }} />
      {expanded && <ImageViewer src={src} alt={alt} onClose={() => setExpanded(false)} />}
    </>
  );
}

function FileBadge({ fileName }) {
  const ext = (fileName || '').split('.').pop()?.toUpperCase() || 'FILE';
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      background: '#151515', border: '1px solid #272736', borderRadius: 4,
      padding: '4px 10px', fontSize: 11, color: '#666', fontFamily: 'var(--font-serif)',
    }}>
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="1" y="1" width="10" height="10" rx="1.5"/><path d="M3 4h6M3 6h6M3 8h4"/>
      </svg>
      {ext}
    </div>
  );
}

function MessageContent({ m, i, isMobile, onHtmlBlock, supabase, userId }) {
  const [showMarkdownModal, setShowMarkdownModal] = useState(false);
  const [markdownPreview, setMarkdownPreview] = useState('');

  // ── Grounded answer (Phase 3 Trust) ─────────────────────
  // Messages with the SHIFT_GROUNDED_V1: prefix carry a structured
  // payload (claim or analysis) instead of prose. Decode and render
  // as a GroundedAnswerCard with confidence badge + source chips.
  // Falls through to normal rendering if decoding fails.
  if (typeof m.content === 'string' && isGroundedContent(m.content)) {
    const payload = decodeGrounded(m.content);
    if (payload) return <GroundedAnswerCard payload={payload} />;
  }

  // PDF attachment
  if (m.isPdf && m.pdfName) return (
    <FileBadge fileName={m.pdfName} />
  );

  // Live image with base64 data — 80x80 clickable.
  // Strip [img:url]/[file:url] marker prefix from m.content so the
  // optimistic render shows only the user's typed caption (or nothing).
  // Without this, workstation-chat-panel's marker-prefixed content
  // would render the literal "[img:https://...]" string under the image.
  if (m.isImage && m.imageData) {
    const captionRaw = typeof m.content === 'string' ? m.content : '';
    const caption = captionRaw
      .replace(/^\[img:[^\]]+\]\s*/, '')
      .replace(/^\[file:[^\]]+\]\s*/, '');
    return (
      <div>
        <ClickableImage src={`data:${m.mimeType};base64,${m.imageData}`} alt="" thumbStyle={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 6, display: 'block' }} />
        {caption && <div style={{ fontSize: 14, color: '#e0e0e0', marginTop: 6 }}>{caption}</div>}
      </div>
    );
  }

  if (m.displayContent) return <>{m.displayContent}</>;

  const rawText = Array.isArray(m.content)
    ? (m.content.find(b => b.type === 'text')?.text || '')
    : (m.content || '');

  // Detect [img:filename] or [file:filename] markers from DB-saved messages
  const imgMatch = rawText.match(/^\[img:([^\]]+)\]\s*(.*)/s);
  const fileMatch = !imgMatch && rawText.match(/^\[file:([^\]]+)\]\s*(.*)/s);
  if (imgMatch) {
    const [, imageRef, text] = imgMatch;
    const isUrl = imageRef.startsWith('http');
    return (
      <div>
        {isUrl ? (
          <ClickableImage src={imageRef} alt="" thumbStyle={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 6, display: 'block' }} />
        ) : (
          <div style={{
            width: 80, height: 80, background: '#151515', borderRadius: 6,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="1.2">
              <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/>
            </svg>
          </div>
        )}
        {text && <div style={{ fontSize: 14, color: '#e0e0e0', marginTop: 6 }}>{text}</div>}
      </div>
    );
  }
  if (fileMatch) {
    const [, fileName, text] = fileMatch;
    return (
      <div>
        <FileBadge fileName={fileName} />
        {text && <div style={{ fontSize: 14, color: '#e0e0e0', marginTop: 6 }}>{text}</div>}
      </div>
    );
  }

  // No code block — render normally
  // User messages — raw
  if (m.role !== 'assistant') {
    return <>{rawText}</>;
  }

  // Assistant message — always apply paragraph breaking + code block handling
  const normalized = rawText.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const parts = normalized.split('```');

  const result = [];

  // Break a prose string into readable chunks — max 2 sentences per paragraph
  function breakIntoParagraphs(text) {
    const withBreaks = text.replace(/\\n/g, '\n');
    if (withBreaks.includes('\n')) {
      const lines = withBreaks.split('\n');
      const result = [];
      lines.forEach(line => {
        const t = line.trim();
        if (!t) return;
        const sentences = t.match(/[^.!?]*[.!?]+["']?\s*/g) || [t];
        for (let i = 0; i < sentences.length; i += 2) {
          const chunk = sentences.slice(i, i + 2).join('').trim();
          if (chunk) result.push(chunk);
        }
      });
      return result.filter(c => c.length > 0);
    }
    let norm = text
      .replace(/\s+-\s+(?=[A-Z])/g, '\n- ')
      .replace(/\s+-\s+(?=\*\*)/g, '\n- ');
    const segments = norm.split(/(?=\*\*[^*]+\*\*)/g);
    const result = [];
    segments.forEach(segment => {
      const trimmed = segment.trim();
      if (!trimmed) return;
      const sentences = trimmed.match(/[^.!?]*[.!?]+["']?\s*/g) || [trimmed];
      for (let i = 0; i < sentences.length; i += 2) {
        const chunk = sentences.slice(i, i + 2).join('').trim();
        if (!chunk) continue;
        if (chunk.length > 180 && /[—:;]/.test(chunk)) {
          const subchunks = chunk.split(/(?<=[—:;])\s+/);
          subchunks.forEach(s => { if (s.trim()) result.push(s.trim()); });
        } else {
          result.push(chunk);
        }
      }
    });
    return result.filter(c => c.length > 0);
  }

  // Inline markdown: `code`, **bold**, and *italic* in a single split pass.
  // The regex order matters: **bold** must be checked before *italic*
  // to avoid matching the first * of a **bold** pair as an italic start.
  //
  // NEUTRAL EMPHASIS — no color accents in content. Brightness = emphasis.
  // Bold goes to pure white (brightest), italic is slant only (no color),
  // inline code is neutral-bright (no gold tint). See tokens.css for the
  // --md-* variable definitions.
  function renderProseString(text, keyPrefix) {
    const segments = text.split(/(`[^`\n]+`|\*\*[^*\n]+\*\*|(?<!\*)\*[^*\n]+\*(?!\*))/g);
    return segments.map((seg, si) => {
      if (!seg) return null;
      if (seg.startsWith('`') && seg.endsWith('`') && seg.length > 2) {
        return <code key={keyPrefix + si} style={{ fontFamily: "'Courier New', monospace", fontSize: '0.88em', background: '#1e1e2a', border: '1px solid #2a2a2a', borderRadius: '3px', padding: '1px 5px', color: 'var(--md-inline-code, #e8e8e8)' }}>{seg.slice(1, -1)}</code>;
      }
      if (seg.startsWith('**') && seg.endsWith('**') && seg.length > 4) {
        // Same color as body. Weight 700 carries the emphasis — visible
        // ink density shift without any brightness change.
        return <strong key={keyPrefix + si} style={{ color: 'var(--md-bold, #d4d4d4)', fontWeight: 700 }}>{seg.slice(2, -2)}</strong>;
      }
      if (seg.startsWith('*') && seg.endsWith('*') && seg.length > 2) {
        // Italic = slant only, no color change. Soft inline emphasis.
        return <em key={keyPrefix + si} style={{ fontStyle: 'italic' }}>{seg.slice(1, -1)}</em>;
      }
      return <span key={keyPrefix + si}>{seg}</span>;
    }).filter(Boolean);
  }

  // Line-level markdown: headings (#, ##, ###), bullets (-, *), and
  // blockquotes (>). Consecutive bullet lines group into a single <ul>.
  // Consecutive quote lines group into a single <blockquote>. Regular
  // lines pass through to renderProseString for inline formatting.
  //
  // NEUTRAL EMPHASIS — no color accents. Headings use brightness tiers
  // (h1/h2 pure white, h3 slightly dimmer). Blockquote uses dimmer gray
  // with a left border rule for "set apart" visual treatment.
  function renderLines(lines, keyPrefix) {
    const out = [];
    let bulletBuf = [];
    let quoteBuf = [];

    function flushBullets() {
      if (bulletBuf.length === 0) return;
      const buf = bulletBuf;
      bulletBuf = [];
      out.push(
        <ul
          key={keyPrefix + 'ul-' + out.length}
          className="shift-md-bullets"
          style={{
            margin: '4px 0 12px 0', paddingLeft: 20,
            color: '#d4d4d4', listStyle: 'disc',
          }}
        >
          {buf.map((b, bi) => (
            <li key={bi} style={{ marginBottom: 4, lineHeight: 1.65 }}>
              {renderProseString(b, keyPrefix + 'b' + bi + '-')}
            </li>
          ))}
        </ul>
      );
    }

    function flushQuotes() {
      if (quoteBuf.length === 0) return;
      const buf = quoteBuf;
      quoteBuf = [];
      out.push(
        <blockquote
          key={keyPrefix + 'bq-' + out.length}
          style={{
            margin: '12px 0',
            padding: '2px 0 2px 14px',
            borderLeft: '2px solid var(--md-blockquote-border, #333333)',
            color: 'var(--md-blockquote, #a8a8a8)',
            fontStyle: 'italic',
            fontFamily: 'Georgia, serif',
            lineHeight: 1.65,
          }}
        >
          {buf.map((q, qi) => (
            <div key={qi} style={{ marginBottom: qi < buf.length - 1 ? 6 : 0 }}>
              {renderProseString(q, keyPrefix + 'q' + qi + '-')}
            </div>
          ))}
        </blockquote>
      );
    }

    function flushAll() {
      flushBullets();
      flushQuotes();
    }

    lines.forEach((rawLine, li) => {
      const line = rawLine.trim();
      if (!line) { flushAll(); return; }

      // Blockquote line — collect into buffer
      const quoteMatch = line.match(/^>\s?(.*)/);
      if (quoteMatch) {
        flushBullets();
        quoteBuf.push(quoteMatch[1]);
        return;
      }

      // Bullet line — collect into buffer
      const bulletMatch = line.match(/^[-*]\s+(.+)/);
      if (bulletMatch) {
        flushQuotes();
        bulletBuf.push(bulletMatch[1]);
        return;
      }

      flushAll();

      // H2 — ## Title (body color, larger + bolder + margin)
      if (line.startsWith('## ')) {
        out.push(
          <div
            key={keyPrefix + 'h2-' + li}
            style={{
              fontSize: '1.1em', fontWeight: 700,
              color: 'var(--md-heading-2, #d4d4d4)',
              fontFamily: 'Georgia, serif', margin: '18px 0 8px 0',
            }}
          >
            {renderProseString(line.slice(3).trim(), keyPrefix + 'h2t-' + li + '-')}
          </div>
        );
        return;
      }

      // H3 — ### Title (body color, smaller than H2, tracked for subtle distinction)
      if (line.startsWith('### ')) {
        out.push(
          <div
            key={keyPrefix + 'h3-' + li}
            style={{
              fontSize: '0.98em', fontWeight: 700,
              color: 'var(--md-heading-3, #d4d4d4)',
              fontFamily: 'Georgia, serif', margin: '14px 0 6px 0',
              letterSpacing: '0.02em',
            }}
          >
            {renderProseString(line.slice(4).trim(), keyPrefix + 'h3t-' + li + '-')}
          </div>
        );
        return;
      }

      // H1 — # Title (rare — body color, biggest size)
      if (line.startsWith('# ')) {
        out.push(
          <div
            key={keyPrefix + 'h1-' + li}
            style={{
              fontSize: '1.2em', fontWeight: 700,
              color: 'var(--md-heading-1, #d4d4d4)',
              fontFamily: 'Georgia, serif', margin: '20px 0 10px 0',
            }}
          >
            {renderProseString(line.slice(2).trim(), keyPrefix + 'h1t-' + li + '-')}
          </div>
        );
        return;
      }

      // Regular prose line
      out.push(
        <div key={keyPrefix + 'p-' + li} style={{ marginBottom: 10 }}>
          {renderProseString(line, keyPrefix + 'p' + li + '-')}
        </div>
      );
    });

    flushAll();
    return out;
  }

  parts.forEach((part, idx) => {
    if (idx % 2 === 0) {
      const trimmed = part.replace(/^\n+/, '').replace(/\n+$/, '');
      if (trimmed.trim()) {
        const hasExplicitBreaks = trimmed.includes('\n');
        if (hasExplicitBreaks) {
          const lines = trimmed.split('\n');
          result.push(
            <div key={'t-' + idx}>
              {renderLines(lines, 't-' + idx + '-')}
            </div>
          );
        } else {
          const paragraphs = breakIntoParagraphs(trimmed);
          result.push(
            <div key={'t-' + idx}>
              {paragraphs.map((para, pi) => (
                <div key={pi} style={{ marginBottom: paragraphs.length > 1 ? '10px' : '6px' }}>
                  {renderProseString(para, 't-' + idx + '-' + pi + '-')}
                </div>
              ))}
            </div>
          );
        }
      }
    } else {
      const newlineIdx = part.indexOf('\n');
      if (newlineIdx < 0) return;
      const lang = part.slice(0, newlineIdx).trim().toLowerCase();
      const code = part.slice(newlineIdx + 1).replace(/\n$/, '');
      const label = LANG_LABELS[lang] || (lang ? lang.toUpperCase() : 'Code');
      const isHtml = lang === 'html' || lang === 'htm';
      const isMarkdown = lang === 'markdown' || lang === 'md';
      const lineCount = code.split('\n').length;

      function getCodeDesc() {
        const lines = code.split('\n').slice(0, 8);
        for (const l of lines) {
          const comment = l.match(/^[/#*\s]{0,4}(?:#|\/\/)\s*(.+)/);
          if (comment && comment[1].length > 3 && comment[1].length < 60) return comment[1].trim();
        }
        const fn = code.match(/(?:def|function|class|const|let|var)\s+([a-zA-Z_][a-zA-Z0-9_]*)/);
        if (fn) return fn[1].replace(/_/g, ' ');
        return null;
      }
      const codeDesc = getCodeDesc();

      function handleCopy(e) {
        const btn = e.currentTarget;
        navigator.clipboard.writeText(code).then(() => {
          btn.textContent = 'copied';
          btn.style.color = '#4a7a6d';
          setTimeout(() => { btn.textContent = 'copy'; btn.style.color = '#888'; }, 1800);
        });
      }

      result.push(
        <div key={'pill-' + idx} style={{ display: 'flex', flexDirection: 'column', gap: '0', margin: '4px 0 2px', background: '#111', border: '1px solid #2a2a2a', borderRadius: '8px', overflow: 'hidden', fontSize: '12px', fontFamily: 'monospace' }}>
          {codeDesc && (
            <div style={{ padding: '8px 14px 0', color: '#888', fontSize: '12px', fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>{codeDesc}</div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: codeDesc ? '6px 14px 8px' : '8px 14px' }}>
            <span style={{ color: '#666', letterSpacing: '0.06em' }}>{label}</span>
            <span style={{ color: '#333' }}>·</span>
            <span style={{ color: '#555' }}>{lineCount} lines</span>
            {isHtml && onHtmlBlock && (
              <button onClick={() => { if (onHtmlBlock) onHtmlBlock(code); }}
                style={{ background: 'none', border: '1px solid #2a3a2a', cursor: 'pointer', color: '#4a7a4a', fontSize: '11px', fontFamily: 'Georgia, serif', letterSpacing: '0.06em', padding: '2px 8px', borderRadius: '3px' }}
                onMouseEnter={e => e.currentTarget.style.background = '#1a2a1a'}
                onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                ▶ preview
              </button>
            )}
            {isMarkdown && (
              <button onClick={() => { setMarkdownPreview(code); setShowMarkdownModal(true); }}
                style={{ background: 'none', border: '1px solid #2a2a2a', cursor: 'pointer', color: '#888', fontSize: '11px', fontFamily: 'Georgia, serif', letterSpacing: '0.06em', padding: '2px 8px', borderRadius: '3px' }}
                onMouseEnter={e => e.currentTarget.style.color = '#bbb'}
                onMouseLeave={e => e.currentTarget.style.color = '#888'}>
                View
              </button>
            )}
            <button onClick={handleCopy}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', fontSize: '11px', fontFamily: 'Georgia, serif', letterSpacing: '0.06em', padding: '2px 4px' }}
              onMouseEnter={e => e.currentTarget.style.color = '#bbb'}
              onMouseLeave={e => e.currentTarget.style.color = '#888'}>
              copy
            </button>
            {lineCount > 5 && (
              <button onClick={() => downloadCode(code, lang)}
                style={{ background: 'none', border: '1px solid #2a2a2a', cursor: 'pointer', color: '#666', fontSize: '11px', fontFamily: 'Georgia, serif', letterSpacing: '0.06em', padding: '2px 8px', borderRadius: '3px' }}
                onMouseEnter={e => { e.currentTarget.style.color = '#999'; e.currentTarget.style.borderColor = '#444'; }}
                onMouseLeave={e => { e.currentTarget.style.color = '#666'; e.currentTarget.style.borderColor = '#2a2a2a'; }}>
                ↓ download
              </button>
            )}
          </div>
        </div>
      );
    }
  });

  return (
    <>
      {/* Hyphenation + reading comfort: break long words at syllables
          to prevent awkward right margins, especially for the warm
          Literata font + narrow chat measure. Applied to the whole
          rendered content tree via style, not a class, so every
          message automatically gets it. */}
      <div style={{ hyphens: 'auto', WebkitHyphens: 'auto', msHyphens: 'auto' }}>
        {result}
      </div>
      {/* Muted gray bullet markers — lets the content dominate, not
          the disc. Uses ::marker so it only colors the disc, not the
          text. Scoped to .shift-md-bullets so other lists on the
          page are unaffected. Part of the neutral emphasis system. */}
      <style jsx global>{`
        .shift-md-bullets > li::marker {
          color: var(--md-bullet-marker, #666666);
        }
      `}</style>
      {showMarkdownModal && (
        <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, left: 'var(--sidebar-width, 50px)', background: 'rgba(0,0,0,0.75)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
          onClick={() => setShowMarkdownModal(false)}>
          <div style={{ background: '#07070a', border: '1px solid #272736', borderRadius: 8, width: '100%', maxWidth: 680, maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #1e1e2a', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 12, color: '#888', fontFamily: 'Georgia, serif' }}>Document</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button onClick={() => { const b = new Blob([markdownPreview], { type: 'text/markdown' }); const u = URL.createObjectURL(b); const a = document.createElement('a'); a.href = u; a.download = 'document.md'; a.click(); URL.revokeObjectURL(u); }}
                  style={{ background: 'none', border: '1px solid #272736', borderRadius: 3, padding: '3px 10px', fontSize: 11, color: '#7a9e82', cursor: 'pointer', fontFamily: 'Georgia, serif' }}>
                  ↓ Download
                </button>
                <div onClick={() => setShowMarkdownModal(false)} style={{ fontSize: 18, color: '#555', cursor: 'pointer', lineHeight: 1 }}>×</div>
              </div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: 24, fontSize: 13, color: '#ccc', lineHeight: 1.8, fontFamily: 'Georgia, serif', whiteSpace: 'pre-wrap' }}>
              {markdownPreview}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default memo(MessageContent);
